import React, { useMemo } from 'react'
import {
  appendUnique,
  splitOnSeparators,
} from 'sutra/components/MultiSelect/multi-select-options'
import {
  creatableText,
  filterOptions,
  optionText,
  resolveToken,
} from 'sutra/components/Select/select-options'
import type {
  SelectCommonProps,
  SelectDomAttributes,
  SelectMultipleProps,
  SelectOption,
  SelectValue,
} from 'sutra/components/Select/select-types'
import { useMergedRef } from 'sutra/components/Select/use-merged-ref'
import { useSelectOpen } from 'sutra/components/Select/use-select-open'
import {
  type SelectSearch,
  useSelectSearch,
} from 'sutra/components/Select/use-select-search'
import { useSelectValue } from 'sutra/components/Select/use-select-value'

export type MultiSelectedProps<
  T extends SelectValue,
  O extends SelectOption<T>,
> = SelectCommonProps<T, O> &
  SelectMultipleProps<T, O> & {
    domAttributes: SelectDomAttributes
    forwardedRef: React.Ref<HTMLElement>
  }

export interface MultiSelectState<
  T extends SelectValue,
  O extends SelectOption<T>,
> {
  values: T[]
  items: T[]
  isOpen: boolean
  atMax: boolean
  optionByValue: Map<SelectValue, O>
  textOf: (value: T) => string
  toOption: (value: T) => O
  search: SelectSearch
  setInputRef: React.RefCallback<HTMLInputElement>
  commit: (next: T[]) => void
  handleInput: (text: string, reason: string) => void
  handleOpenChange: (next: boolean) => void
  emptyText: React.ReactNode
}

// Stable, so a `null` value does not look like a new value on every render.
const NONE: never[] = []
const NO_SEPARATORS: string[] = []

// State and handlers of `multiple`/`tags`: a Combobox in `multiple` mode whose
// items are the values themselves (the free-text ones of `tags` included).
export const useSelectMulti = <
  T extends SelectValue,
  O extends SelectOption<T>,
>(
  props: MultiSelectedProps<T, O>,
): MultiSelectState<T, O> => {
  const all = props.options as readonly O[]
  const tags = props.mode === 'tags'
  const [current, setCurrent] = useSelectValue<T[]>(
    props.value === null ? NONE : props.value,
    props.defaultValue,
  )
  const values = current ?? NONE
  const { isOpen, setOpen, focusRef } = useSelectOpen<HTMLInputElement>(props)
  const search = useSelectSearch(props)
  const setInputRef = useMergedRef(focusRef, props.forwardedRef)
  const { maxCount } = props

  const optionByValue = useMemo(
    () => new Map<SelectValue, O>(all.map((option) => [option.value, option])),
    [all],
  )
  const atMax = maxCount !== undefined && values.length >= maxCount
  const toOption = (value: T): O =>
    optionByValue.get(value) ?? ({ value, label: String(value) } as O)
  const textOf = (value: T): string =>
    optionText(toOption(value), props.optionFilterProp)

  const createValue =
    tags && !atMax
      ? (creatableText(search.query, all, values) as T | null)
      : null
  const visible = filterOptions(all, {
    query: search.query,
    filterOption: props.filterOption,
    filterSort: props.filterSort,
    optionFilterProp: props.optionFilterProp,
  })
  // "Create x" goes last, so Enter on a query with a real match picks it.
  const items = [
    ...visible.map((option) => option.value as T),
    ...(createValue === null ? [] : [createValue]),
  ]

  const commit = (next: T[]): void => {
    const capped = maxCount === undefined ? next : next.slice(0, maxCount)
    setCurrent(capped)
    props.onChange?.(capped, capped.map(toOption))
  }

  // Only a close clears the query: a caller holding `open={false}` still gets
  // an open request on every keystroke, which must not wipe what was typed.
  const handleOpenChange = (next: boolean): void => {
    if (!next) search.resetQuery()
    setOpen(next)
  }

  // Typed or pasted text containing a separator becomes values.
  const handleInput = (text: string, reason: string): void => {
    const split = tags
      ? splitOnSeparators(text, props.tokenSeparators ?? NO_SEPARATORS)
      : null
    if (!split || reason !== 'input-change') {
      search.setQuery(text, reason)
      return
    }
    const tokens = split.tokens.map((token) => resolveToken(token, all) as T)
    const merged = appendUnique(values, tokens)
    if (merged.length !== values.length) commit(merged)
    search.setQuery(split.rest, reason)
  }

  return {
    values,
    items,
    isOpen,
    atMax,
    optionByValue,
    textOf,
    toOption,
    search,
    setInputRef,
    commit,
    handleInput,
    handleOpenChange,
    emptyText:
      props.notFoundContent === undefined
        ? props.loading
          ? 'Loading…'
          : 'No matches'
        : props.notFoundContent,
  }
}
